/// <amd-module name="DistributedTaskUI/SecurityComponent/UserPermissionsAuthorization/Components/AddUserDialog" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/SecurityComponent/Resources";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import Roles_Contracts = require("SecurityRoles/Client/Contracts/SecurityRoles");
import { IdentitiesSearchRequestModel, IIdentity } from "VSS/Features/Identities/IdentityService";
import { Dialog } from "VSSUI/Dialog";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IdentityPickerDropdown, IPeoplePickerProvider } from "VSSUI/IdentityPicker";
import { Dropdown } from "VSSUI/Dropdown";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IListBoxItem } from "VSSUI/ListBox";
import { FormItem } from "VSSUI/FormItem";
import { ObservableValue } from "VSS/Core/Observable";

interface IAddUserDialogState {
    showDialog: boolean;
    disableOkButton: boolean;
    selectedRole: Roles_Contracts.SecurityRole;
    userid: IIdentity;
    hideRoleDescription: boolean;
}

interface IAddUserDialogProps {
    addCallback: (roleRefs: Roles_Contracts.UserRoleAssignmentRef[]) => Promise<void>;
    title?: string;
    roles: Roles_Contracts.SecurityRole[];
    defaultRole?: string;
    hideRoleDescriptions?: boolean;
    addUserDialogUserLabel?: string;
    onCancel: () => void;
    identityTypes: string[];
    operationScopes: string[];
}

export class AddUserDialog extends VssComponent<IAddUserDialogProps, IAddUserDialogState> {
    public constructor(props: IAddUserDialogProps, context: IVssComponentContext) {
        super(props);
        let selectedRole = this.props.roles[0];
        if (!!this.props.defaultRole) {
            let roles = this.props.roles.filter(r => r.name === this.props.defaultRole);
            if (roles && roles.length > 0) {
                selectedRole = roles[0];
            }
        }

        let hideRoleDescription = !!this.props.hideRoleDescriptions ? this.props.hideRoleDescriptions : false;

        this.state = {
            showDialog: true,
            disableOkButton: true,
            selectedRole: selectedRole,
            userid: null,
            hideRoleDescription: hideRoleDescription
        };
        this.pickerProvider = new PeoplePickerProvider(context.pageContext, {
            autoFocus: true,
            identityTypes: this.props.identityTypes,
            operationScopes: this.props.operationScopes,
            options: {
                MinResults: 5,
                MaxResults: 20
            }
        } as Partial<IdentitiesSearchRequestModel>);
        this.roleDropdownSelection = new DropdownSelection();
        this.options = this._getRoles();
    }

    public render(): JSX.Element {
        this.props.roles.map((r, index) => {
            if (r.displayName === this.state.selectedRole.displayName) {
                this.roleDropdownSelection.select(index);
            }
        });
        return (
            <Dialog
                className="add-user-dialog"
                titleProps={{ text: Resources.AddUsersDialogTitle }}
                onDismiss={() => { this.props.onCancel(); }}
                footerButtonProps={[
                    {
                        className: "cta",
                        primary: true,
                        text: Resources.AddButtonText,
                        onClick: () => {
                            this._tryAddUser();
                        },
                        disabled: !this.state.userid
                    },
                    {
                        className: "cta",
                        text: Resources.CloseButtonLabelText,
                        onClick: () => {
                            this.props.onCancel();
                        }
                    }
                ]}>
                <table className="add-user-container" role="presentation">
                    <tbody>
                        <tr className="user-row-input">
                            <td className="label">
                                <label htmlFor="identitySearch">{this._getAddUserInputFieldLabel()}</label>
                            </td>
                            <td>
                                <div className="identity-picker-container">
                                    <FormItem className="identity-picker-label" label={this._getAddUserInputFieldLabel()}>
                                        <IdentityPickerDropdown
                                            className="feed-identity-picker-dropdown"
                                            suggestionsItemClassName="suggestions"
                                            pickerProvider={this.pickerProvider}
                                            onChange={(identity?: IIdentity) => {
                                                this.setState({ userid: identity });
                                                this.selectedIdentity.value = identity;
                                            }}
                                            value={this.selectedIdentity}
                                        />
                                    </FormItem>
                                </div>
                            </td>
                        </tr>
                        <tr className="role-row-input">
                            <td className="label">
                                <label htmlFor="role">
                                    {Resources.AddRoleInputField}</label>
                            </td>
                            <td>
                                <div className="select-roles-menu">
                                    <Dropdown
                                        ariaLabel={Resources.AddRoleInputField.concat(this.state.selectedRole.displayName).concat(this.state.selectedRole.description)}
                                        items={this.options}
                                        onSelect={(event: React.SyntheticEvent<HTMLElement>, selection?: IListBoxItem) => {
                                            if (selection) {
                                                this._onRoleChanged(null, selection);
                                            }
                                        }}
                                        selection={this.roleDropdownSelection}
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr className="role-description-row" >
                            <td className="label">
                                <label htmlFor="description"></label>
                            </td>
                            <td>
                                <div className="role-description-container" style={{ display: this.state.hideRoleDescription ? "none" : "inline-block" }}>
                                    <div id="description" className="permission-description">{this.state.selectedRole.description}</div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

            </Dialog>
        );
    }

    private _tryAddUser(): void {
        let roleRefs = [];
        roleRefs.push({
            roleName: this.state.selectedRole.name,
            userId: this.state.userid.localId
        } as Roles_Contracts.UserRoleAssignmentRef);
        this.props.addCallback(roleRefs);
    }

    private _getAddUserInputFieldLabel(): string {
        let inputFieldLabel: string = Resources.AddUserInputField;
        if (!!this.props.addUserDialogUserLabel) {
            inputFieldLabel = this.props.addUserDialogUserLabel;
        }

        return inputFieldLabel;
    }

    private _getRoles(): IListBoxItem[] {
        let options: IListBoxItem[] = [];
        this.props.roles.map(role => {
            options.push({
                id: role.displayName,
                text: role.displayName
            });
        });
        return options;
    }

    private _onRoleChanged(event: React.SyntheticEvent<HTMLElement>, selectedOption: IListBoxItem) {
        this.props.roles.map(r => {
            if (r.displayName === selectedOption.id) {
                this.setState({ selectedRole: r });
            }
        });
    }

    private roleDropdownSelection: DropdownSelection;
    private options: IListBoxItem[] = [];
    private pickerProvider: IPeoplePickerProvider;
    private selectedIdentity: ObservableValue<IIdentity | undefined> = new ObservableValue<IIdentity | undefined>(undefined);
}
